import { apiClient } from '@/core/api/client';
import type {
  ConversationsResponse,
  ConversationResponse,
  QueueSMSRequest,
  QueueSMSResponse,
  SMSTemplate
} from '../types/sms.types';

const BASE_URL = '/api/sms-gateway/admin';

export const smsAPI = {
  /**
   * Get all conversations (grouped by phone number)
   */
  async getConversations(params?: {
    limit?: number;
    offset?: number;
    search?: string;
  }): Promise<ConversationsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    if (params?.search) searchParams.append('search', params.search);

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/conversations${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<ConversationsResponse>(url);
  },

  /**
   * Get conversation messages for a specific phone number
   */
  async getConversation(
    phoneNumber: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ConversationResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const queryString = searchParams.toString();
    const encodedPhone = encodeURIComponent(phoneNumber);
    const url = `${BASE_URL}/conversations/${encodedPhone}${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<ConversationResponse>(url);
  },

  /**
   * Send a new SMS
   */
  async sendSMS(data: QueueSMSRequest): Promise<QueueSMSResponse> {
    return apiClient.post<QueueSMSResponse>(`${BASE_URL}/queue`, data);
  },

  /**
   * Get SMS templates
   */
  async getTemplates(params?: {
    category?: string;
    active?: boolean;
  }): Promise<{ success: boolean; data: SMSTemplate[] }> {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.active !== undefined) searchParams.append('active', params.active.toString());

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/templates${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<{ success: boolean; data: SMSTemplate[] }>(url);
  },

  /**
   * Mark inbox message as processed
   */
  async markAsProcessed(
    messageId: string,
    data?: { notes?: string; affaireId?: number; clientId?: number }
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.put<{ success: boolean; message: string }>(
      `${BASE_URL}/inbox/${messageId}/process`,
      data || {}
    );
  },

  /**
   * Mark all unread messages in a conversation as read
   */
  async markConversationRead(
    phoneNumber: string
  ): Promise<{ success: boolean; message: string; data: { markedCount: number } }> {
    const encodedPhone = encodeURIComponent(phoneNumber);
    return apiClient.put<{ success: boolean; message: string; data: { markedCount: number } }>(
      `${BASE_URL}/conversations/${encodedPhone}/mark-read`,
      {}
    );
  },

  /**
   * Get gateway status
   */
  async getStatus(): Promise<{
    success: boolean;
    data: {
      gateways: Array<{
        gateway_id: string;
        status: string;
        last_heartbeat: string;
      }>;
      outboxStats: {
        pending: number;
        sent: number;
        failed: number;
      };
      inboxStats: {
        total: number;
        unprocessed: number;
      };
    };
  }> {
    return apiClient.get(`${BASE_URL}/status`);
  }
};

export default smsAPI;
